import "./MedicalStaff.css";

import React, { useEffect, useState, useMemo, useRef } from "react";

import MedicalStaffStatusTag from "../components/MedicalStaffStatusTag";

import { Card } from "primereact/card";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import DoctorList from "./DoctorList";

import Dashboard from "../Layout/Dashboard";

import { MedicalStaffStatus } from "../constants";

import { useCitaMedicaDoctors, useCitaMedicaSpecialties } from "../hooks/queries";
import { useDoctorsMutation } from "../hooks/mutations";

/**
 * Listado de doctores del hospital y su capacidad de pintarlos
 */
export default function MainMedicalStaffSelector() {
 
  const [assignedFilter, setAssignedFilter] = useState(null); // null, true, or false for filtering
  const [selectAll, setSelectAll] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [showConfirmedDoctorsDialog, setShowConfirmedDoctorsDialog] = useState(false);
  const toastRef = useRef(null);

  const doctorsQuery = useCitaMedicaDoctors();

  const specialitiesQuery = useCitaMedicaSpecialties();

  const doctorMutation = useDoctorsMutation({
    onSuccess: ({ type, message }) => {
      if (type === "success") {

        toastRef.current.show({
          severity: "success",
          summary: "Éxito",
          detail: message,
        });

        setShowConfirmedDoctorsDialog(false);

      } else {

        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: message,
        });
        
      }
      
      setShowConfirmedDoctorsDialog(false);
    },
  });

  useEffect(() => {

    setAssignedDoctors(
      (doctorsQuery.data?.results ?? [])
        .filter((doctor) => doctor.is_assigned)
        .map((doctor) => doctor.id)
    );

    setSpecialties(specialitiesQuery.data ?? []);

  }, [doctorsQuery.data, specialitiesQuery.data]);

  const totalDoctors = doctorsQuery.data?.count ?? 0;
  const totalAssigned = assignedDoctors.length;
  const totalNotAssigned = totalDoctors - totalAssigned;

  // Filter doctors based on selected specialties and assigned status
  const filteredDoctors = useMemo(() => {
    return (doctorsQuery.data?.results ?? []).filter((doctor) => {

      const matchesSpecialty =
        selectedSpecialties.length === 0 ||
        doctor.specialities.some((specialty) =>
          selectedSpecialties.includes(specialty.id)
        );

      const matchesAssignedStatus =
        assignedFilter === null ||
        (assignedFilter === true && assignedDoctors.includes(doctor.id)) ||
        (assignedFilter === false && !assignedDoctors.includes(doctor.id));

      return matchesSpecialty && matchesAssignedStatus;
    });

  }, [selectedSpecialties, assignedFilter, assignedDoctors, doctorsQuery.data]);

  const allFilteredAssigned = filteredDoctors.every(
    doc => assignedDoctors.includes(doc.id)
  );

  useEffect(() => {

    // Update selectAll state based if all filtered doctors are assigned
    if (filteredDoctors.length === 0) {
      setSelectAll(false);
      return;
    }
    
    setSelectAll(allFilteredAssigned);

  }, [assignedDoctors, filteredDoctors, allFilteredAssigned]);

  // When selectAll changes, assign/unassign all filtered doctors
  const handleSelectAllChange = (e) => {

    const isChecked = e.checked;
    setSelectAll(isChecked);

    setAssignedDoctors((prevAssigned) => {
      const filteredIds = filteredDoctors.map(doctor => doctor.id);

      if (isChecked) {
        // Add all filtered doctor IDs to assignedDoctors if not already present
        const newAssigned = [...new Set([...prevAssigned, ...filteredIds])];
        return newAssigned;
      } else {
        // Remove all filtered doctor IDs from assignedDoctors
        const newAssigned = prevAssigned.filter(id => !filteredIds.includes(id));
        return newAssigned;
      }
    });
  };

  const handleAssign = (doctorId, isAssigned) => {
    setAssignedDoctors((prevAssigned) => {
      if (isAssigned) {
        // Unassign the doctor
        return prevAssigned.filter((id) => id !== doctorId);
      } else {
        // Assign the doctor
        return [...prevAssigned, doctorId];
      }
    });
  };

  const handleSpecialtyChange = (e) => {
    setSelectedSpecialties(e.value);
  };

  const handleFilterClick = (status) => {
    setAssignedFilter(status);
  };

  const saveChanges = () => {

    // Combine assigned and unassigned doctors with their id and is_assigned values as "yes" or "no"
    const combinedDoctors = (doctorsQuery.data?.results ?? []).map((doctor) => ({
      id: doctor.id,
      is_assigned: assignedDoctors.includes(doctor.id) ? "yes" : "no",
    }));

    console.log("Combined Doctors:", combinedDoctors);

    doctorMutation.mutate({
      doctors: combinedDoctors,
    });

    // console.log("Selected Specialties:", selectedSpecialties);
    // console.log("Assigned Filter:", assignedFilter);
    // alert("Cambios guardados exitosamente.");
  };

  return (
    <Dashboard headerTitle="Médicos a bordo">
      <Toast ref={toastRef} position="top-center" />

      {/* Instrucciones para los usuarios */}
      
      <div className="instructions mb-3 p-0">
        <Card className="p-0">
          <h2 className="py-0 my-0">Seleccione los médicos disponibles para atender casos en su Centro Médico.</h2>
        </Card>
      </div>
      {/* Fin de instrucciones para los usuarios */}
      
      <div className="card">
        {/* General Statistics Section */}
        <div className="flex flex-wrap gap-3 mb-2">
          {/* Total card */}
          <Card 
            title="" 
            className={`w-3 my-2 card-count md:w-3 lg:w-2 ${assignedFilter === null ? 'active' : ''}`}
            onClick={() => handleFilterClick(null)}
          >
            <div className="text-center">
              <p className="text-xl m-0">
                <strong>{totalDoctors}</strong>
              </p>
              <MedicalStaffStatusTag value={MedicalStaffStatus.TOTAL} />
            </div>
          </Card>
          {/* End Total card */}

          {/* Assigned card */}
          <Card 
            title="" 
            className={`w-3 my-2 card-count md:w-3 lg:w-2 ${assignedFilter === true ? 'active' : ''}`}
            onClick={() => handleFilterClick(true)}
          >
            <div className="text-center">
              <p className="text-xl m-0">
                <strong>{totalAssigned}</strong>
              </p>
              <MedicalStaffStatusTag value={MedicalStaffStatus.ASIGNADOS} />
            </div>
          </Card>
          {/* End Assigned card */}

          {/* Not assigned card */}
          <Card 
            title="" 
            className={`w-3 my-2 card-count md:w-3 lg:w-2 ${assignedFilter === false ? 'active' : ''}`}
            onClick={() => handleFilterClick(false)}
          >
            <div className="text-center">
              <p className="text-xl m-0">
                <strong>{totalNotAssigned}</strong>
              </p>

              <MedicalStaffStatusTag value={MedicalStaffStatus.SIN_ASIGNAR} />
            </div>
          </Card>
          {/* End Not assigned card */}
        </div>
        {/* End General Statistics Section */}

        {/* Panel de filtros */}
        <div className="flex flex-wrap justify-content-between mb-2">
          {/* Seleccionar doctores */}
          <div className="flex align-items-center">
            <Checkbox
              inputId="selectAll"
              checked={selectAll}
              onChange={handleSelectAllChange}
            />
            <label htmlFor="selectAll" className="ml-2">
              Seleccionar todo
            </label>
          </div>

          {/* Filtrar por especialidades */}
          <div className="flex flex-column md:flex-row md:align-items-center mt-3 mb-3 md:mt-0 md:mb-0">
            <label htmlFor="specialties" className="mr-2">
              Especialidades:
            </label>
            <MultiSelect
              id="specialties"
              value={selectedSpecialties}
              options={specialties}
              onChange={handleSpecialtyChange}
              placeholder="Seleccione especialidades"
              display="chip"
              className="w-full md:w-20rem"
              filter
            />
          </div>
        </div>
        {/* Fin de panel de filtros */}

        {/* Listado de doctores */}
        <div className="flex flex-wrap mb-2">
          <DoctorList
            doctors={filteredDoctors}
            assignedDoctors={assignedDoctors}
            onToggleAssign={handleAssign}
          />
        </div>
        {/* Fin del listado de doctores */}

        {/* Botones de accion */}
        <div className="flex justify-content-end gap-2">
          <button
            className="p-button p-component p-button-secondary"
            onClick={() => {
              setAssignedDoctors(
                (doctorsQuery.data?.results ?? [])
                  .filter((doctor) => doctor.is_assigned)
                  .map((doctor) => doctor.id)
              );
              setSelectedSpecialties([]);
              setAssignedFilter(null);
            }}
          >
            Restaurar
          </button>
          <button
            className="p-button p-component p-button-primary"
            onClick={() => {
              setShowConfirmedDoctorsDialog(true);
            }}
            aria-label="Guardar cambios"
          >
            Guardar
          </button>
        </div>
      </div>

      {/* Dialogo de confirmacion */}
      <Dialog
        header="Confirmación"
        visible={showConfirmedDoctorsDialog}
        style={{ maxWidth: "35rem" }}
        onHide={() => {
          if (showConfirmedDoctorsDialog) setShowConfirmedDoctorsDialog(false);
        }}
        draggable={false}
      >
        <div className="p-fluid text-center">
          <p>
            <b>
              ¿Está seguro de que desea aplicar los cambios a los doctores?
            </b>
          </p>
        </div>
        <br></br>
        <div className="delete-confirm-buttons-container">
          <Button
            type="button"
            label="Confirmar"
            onClick={() => {
              saveChanges();
            }}
          />
          <Button
            type="button"
            label="Cancelar"
            style={{ backgroundColor: "#999999", borderColor: "#999999" }}
            onClick={() => {
              if (showConfirmedDoctorsDialog) 
                setShowConfirmedDoctorsDialog(false);
            }}
          />
        </div>
      </Dialog>
    </Dashboard>
  );
}
